import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import {
  useRemoveWorkspaceUserMutation,
  useResetWorkspaceLocationsMutation,
  useResetWorkspaceUsersMutation,
} from '@frontend/api-workspaces';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useAppScopeCatalog, useAppScopeNaming } from '@frontend/scope';
import { theme } from '@frontend/theme';
import {
  Avatar,
  Button,
  ConfirmationModal,
  IconButton,
  SpinningLoader,
  Table,
  TableColumnConfig,
  Tabs,
  Text,
  Truncated,
  useAlert,
  useModalControl,
  useTooltip,
} from '@frontend/design-system';
import { useLocationGroups } from '../../hooks/use-location-groups';
import { useResolveWorkspaceUsers } from '../../hooks/use-resolve-workspace-users';
import type { UserRow } from '../../hooks/use-resolve-workspace-users';
import { formatOrphanedUsersMessage, useWorkspaceOrphanCheck } from '../../hooks/use-workspace-orphan-check';
import { trackingIds } from '../../tracking-ids';
import type { Workspace } from '../../types';
import { WorkspaceLocationsModal } from '../workspace-form/workspace-locations-step';
import { WorkspaceUsersModal } from '../workspace-form/workspace-users-step';

type Props = {
  workspaceId: string;
  orgId: string;
  workspace: Workspace;
};

type LocationRow = { id: string; name: string; groupName: string };

// Shared table header style — used for both users and locations tables
const tableHeaderStyle = css`
  border-bottom: 1px solid ${theme.colors.neutral40};
  min-height: 36px;
  font-size: ${theme.font.size.small};
  font-weight: ${theme.font.weight.semibold};
  color: ${theme.colors.neutral60};
`;

const usersStyleConfig = {
  columns: [
    { id: '*', headerStyler: tableHeaderStyle },
    {
      id: 'user',
      cellStyler: css`
        border-right: 1px solid ${theme.colors.neutral10};
      `,
    },
  ],
};

const locationsStyleConfig = {
  columns: [
    { id: '*', headerStyler: tableHeaderStyle },
    {
      id: 'groupName',
      cellStyler: css`
        border-right: 1px solid ${theme.colors.neutral10};
      `,
    },
  ],
};

// ── Tooltip-wrapped remove button ─────────────────────────────────────────────
// Used in both users and locations tables to handle last-item protection.

function RemoveActionButton({
  isRemoving,
  isLast,
  lastItemMessage,
  removeLabel,
  onRemove,
}: {
  isRemoving: boolean;
  isLast: boolean;
  lastItemMessage: string;
  removeLabel: string;
  onRemove: () => void;
}) {
  const { Tooltip, tooltipProps, triggerProps } = useTooltip({ placement: 'top' });

  if (isRemoving) {
    return (
      <div css={css({ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40 })}>
        <SpinningLoader size='xs' />
      </div>
    );
  }

  return (
    <>
      <div {...triggerProps}>
        <IconButton
          label={isLast ? lastItemMessage : removeLabel}
          showLabelOnHover={!isLast}
          onClick={onRemove}
          disabled={isLast}
          css={
            isLast
              ? css`
                  opacity: 0.35;
                  cursor: not-allowed;
                `
              : css`
                  cursor: pointer;
                `
          }
        >
          <Icon name='remove2' color='light' />
        </IconButton>
      </div>
      {isLast && <Tooltip {...tooltipProps}>{lastItemMessage}</Tooltip>}
    </>
  );
}

// ── Error Banner ──────────────────────────────────────────────────────────────

function UserResolutionErrorBanner({ onRetry, isRetrying }: { onRetry: () => void; isRetrying: boolean }) {
  const { t } = useTranslation('settings');

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: ${theme.spacing(1.5)};
        padding: ${theme.spacing(1.5, 2)};
        margin-bottom: ${theme.spacing(1.5)};
        background: ${theme.colors.critical5};
        border: 1px solid ${theme.colors.critical20};
        border-radius: ${theme.borderRadius.medium};
      `}
    >
      <Icon
        name='warning-badge'
        color='error'
        css={css`
          flex-shrink: 0;
        `}
      />
      <Text
        size='small'
        css={css`
          flex: 1;
        `}
      >
        {t('Failed to load user details. Some users may appear as "Unknown User".')}
      </Text>
      <Button
        variant='secondary'
        size='small'
        onClick={onRetry}
        disabled={isRetrying}
        css={css`
          flex-shrink: 0;
        `}
      >
        {isRetrying ? <SpinningLoader size='xs' /> : t('Retry')}
      </Button>
    </div>
  );
}

export function WorkspaceDetail({ workspaceId, orgId, workspace }: Props) {
  const { t } = useTranslation('settings');
  const alerts = useAlert();
  const { accessibleLocationData } = useAppScopeCatalog();
  const { getScopeName } = useAppScopeNaming();
  const resetWorkspaceLocationsMutation = useResetWorkspaceLocationsMutation(orgId, workspaceId);
  const resetWorkspaceUsersMutation = useResetWorkspaceUsersMutation(orgId, workspaceId);
  const removeUserMutation = useRemoveWorkspaceUserMutation(orgId, workspaceId);

  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [removingLocationId, setRemovingLocationId] = useState<string | null>(null);

  const addUsersModal = useModalControl();
  const addLocationsModal = useModalControl();
  const removeLocationConfirmModal = useModalControl();

  // ── Location data ─────────────────────────────────────────────────────────
  const { locationsByGroup } = useLocationGroups(orgId);

  const locationRows = useMemo(
    () =>
      workspace.locationIds.map((id) => {
        const data = accessibleLocationData[id];
        const groupId = data?.groupId ?? '';
        return {
          id,
          name: data?.name ?? t('Unknown Location'),
          groupName: getScopeName(groupId, 'group') ?? t('Unknown Group'),
        };
      }),
    [workspace.locationIds, accessibleLocationData, getScopeName, t]
  );

  // ── User data (using new resolution hook) ─────────────────────────────────
  const resolveResult = useResolveWorkspaceUsers({
    locationIds: workspace.locationIds,
    userIds: workspace.userIds ?? [],
    enabled: workspace.locationIds.length > 0,
  });

  const { resolvedUsersMap, assignedUsers } = resolveResult;

  // ── Scroll-based fallback profile resolution ──────────────────────────────
  // When unresolved "Unknown User" rows scroll into view, batch-resolve them.
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const pendingBatchRef = useRef<Set<string>>(new Set());
  const batchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Flush pending batch of unresolved userIds to the resolve hook
  const flushBatch = useCallback(() => {
    if (pendingBatchRef.current.size > 0) {
      resolveResult.resolveUserBatch(Array.from(pendingBatchRef.current));
      pendingBatchRef.current = new Set();
    }
    batchTimerRef.current = null;
  }, [resolveResult.resolveUserBatch]);

  // Set up IntersectionObserver on unresolved rows
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container || resolveResult.unresolvedUserIds.length === 0) return;

    // Clean up previous observer
    observerRef.current?.disconnect();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const userId = (entry.target as HTMLElement).dataset.unresolvedUserId;
            if (userId) {
              pendingBatchRef.current.add(userId);
              // Debounce: collect visible rows for 100ms then flush as one batch
              if (!batchTimerRef.current) {
                batchTimerRef.current = setTimeout(flushBatch, 100);
              }
            }
          }
        }
      },
      { root: container, rootMargin: '100px 0px', threshold: 0 }
    );

    // Observe all unresolved rows
    const unresolvedRows = container.querySelectorAll<HTMLElement>('[data-unresolved-user-id]');
    unresolvedRows.forEach((row) => observer.observe(row));

    observerRef.current = observer;

    return () => {
      observer.disconnect();
      if (batchTimerRef.current) {
        clearTimeout(batchTimerRef.current);
        batchTimerRef.current = null;
      }
    };
  }, [resolveResult.unresolvedUserIds, resolveResult.isBulkQueryLoading, flushBatch]);

  // ── Orphan check ──────────────────────────────────────────────────────────
  const orphanCheck = useWorkspaceOrphanCheck({
    locationIds: workspace.locationIds,
    assignedUserIds: workspace.userIds ?? [],
    resolvedUsersMap,
  });

  // ── Location removal ──────────────────────────────────────────────────────

  const doRemoveLocation = useCallback(
    async (locationId: string, orphanedUserIds: string[] = []) => {
      setRemovingLocationId(locationId);
      try {
        await resetWorkspaceLocationsMutation.mutateAsync({
          locationIds: workspace.locationIds.filter((id) => id !== locationId),
        });
        if (orphanedUserIds.length > 0) {
          await resetWorkspaceUsersMutation.mutateAsync({
            previousUserIds: workspace.userIds ?? [],
            userIds: (workspace.userIds ?? []).filter((id) => !orphanedUserIds.includes(id)),
          });
        }
      } catch {
        alerts.error(t('Failed to remove location. Please try again.'));
      } finally {
        setRemovingLocationId(null);
      }
    },
    [workspace.locationIds, workspace.userIds, resetWorkspaceLocationsMutation, resetWorkspaceUsersMutation, alerts, t]
  );

  const handleRemoveLocation = useCallback(
    (locationId: string) => {
      orphanCheck.checkOrphans(locationId);
    },
    [orphanCheck.checkOrphans]
  );

  // React to orphan-check completion and decide what to do.
  useEffect(() => {
    if (orphanCheck.isCheckError) {
      alerts.error(t('Failed to check location impact. Please try again.'));
      orphanCheck.resetCheck();
      return;
    }

    if (!orphanCheck.isCheckComplete) return;

    const { orphanedUsers, locationIdBeingChecked } = orphanCheck;

    if (orphanedUsers.length === 0) {
      doRemoveLocation(locationIdBeingChecked!);
      orphanCheck.resetCheck();
    } else if (orphanedUsers.length === (workspace.userIds ?? []).length) {
      alerts.error(
        t(
          'Cannot remove this location. All assigned users belong exclusively to it and would be removed from the workspace. Reassign users to another location first.'
        )
      );
      orphanCheck.resetCheck();
    } else {
      removeLocationConfirmModal.openModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orphanCheck.isCheckComplete, orphanCheck.isCheckError]);

  const handleRemoveUser = useCallback(
    async (userId: string) => {
      setRemovingUserId(userId);
      try {
        await removeUserMutation.mutateAsync({ userId });
      } catch {
        alerts.error(t('Failed to remove user. Please try again.'));
      } finally {
        setRemovingUserId(null);
      }
    },
    [removeUserMutation, alerts, t]
  );

  const handleSaveUsers = useCallback(
    async (userIds: string[]) => {
      try {
        await resetWorkspaceUsersMutation.mutateAsync({
          previousUserIds: workspace.userIds ?? [],
          userIds,
        });
        alerts.success(t('Users updated'));
      } catch {
        alerts.error(t('Failed to update users. Please try again.'));
      }
    },
    [workspace.userIds, resetWorkspaceUsersMutation, alerts, t]
  );

  const handleSaveLocations = useCallback(
    async (locationIds: string[]) => {
      try {
        await resetWorkspaceLocationsMutation.mutateAsync({ locationIds });
        alerts.success(t('Locations updated'));
      } catch {
        alerts.error(t('Failed to update locations. Please try again.'));
      }
    },
    [resetWorkspaceLocationsMutation, alerts, t]
  );

  const isLastUser = assignedUsers.length <= 1;
  const isLastLocation = locationRows.length <= 1;

  // Track which userIds are unresolved (for data-attribute marking)
  const unresolvedSet = useMemo(() => new Set(resolveResult.unresolvedUserIds), [resolveResult.unresolvedUserIds]);

  const usersColConfig = useMemo(
    (): TableColumnConfig<UserRow>[] => [
      {
        id: 'user',
        Header: t('User'),
        accessor: 'email',
        sortType: 'alphanumeric',
        cellRenderer: (_, rowData) => {
          const user = rowData!;
          const displayName =
            user.firstName || user.lastName
              ? `${user.firstName} ${user.lastName}`.trim()
              : user.email || t('Unknown User');
          const isUnresolved = unresolvedSet.has(user.id);
          return (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 12px;
              `}
              {...(isUnresolved ? { 'data-unresolved-user-id': user.id } : {})}
            >
              <Avatar name={displayName} size='small' />
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  min-width: 0;
                `}
              >
                <Truncated
                  as={Text}
                  weight='bold'
                  size='medium'
                  tooltipOptions={{ placement: 'top-start' }}
                  css={css`
                    @media (max-width: 445px) {
                      max-width: 80px;
                    }
                  `}
                >
                  {displayName}
                </Truncated>
                {user.email && (
                  <Truncated
                    as={Text}
                    size='small'
                    color='subdued'
                    tooltipOptions={{ placement: 'bottom-start' }}
                    css={css`
                      @media (max-width: 445px) {
                        max-width: 90px;
                      }
                    `}
                  >
                    {user.email}
                  </Truncated>
                )}
              </div>
            </div>
          );
        },
      },
      {
        id: 'actions',
        Header: t('Actions'),
        accessor: 'id',
        disableSortBy: true,
        maxWidth: 85,
        cellAlign: 'center',
        headerAlign: 'center',
        cellRenderer: (_, rowData) => (
          <RemoveActionButton
            isRemoving={
              removingUserId === rowData!.id || (removeUserMutation.isLoading && removingUserId === rowData!.id)
            }
            isLast={isLastUser}
            lastItemMessage={t('A workspace must be assigned to at least one user.')}
            removeLabel={t('Remove user')}
            onRemove={() => handleRemoveUser(rowData!.id)}
          />
        ),
      },
    ],
    [t, handleRemoveUser, removingUserId, isLastUser, removeUserMutation.isLoading, unresolvedSet]
  );

  const locationsColConfig = useMemo(
    (): TableColumnConfig<LocationRow>[] => [
      {
        id: 'name',
        Header: t('Location Name'),
        accessor: 'name',
        sortType: 'alphanumeric',
        cellRenderer: (_, rowData) => (
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
            `}
          >
            <Icon
              name='location-small'
              color='light'
              css={css`
                flex-shrink: 0;
              `}
            />
            <Truncated
              as={Text}
              tooltipOptions={{ placement: 'top-start' }}
              css={css`
                max-width: 220px;
                @media (max-width: 445px) {
                  max-width: 120px;
                }
              `}
            >
              {rowData!.name}
            </Truncated>
          </div>
        ),
      },
      {
        id: 'groupName',
        Header: t('Location Group'),
        accessor: 'groupName',
        sortType: 'alphanumeric',
        cellRenderer: (_, rowData) =>
          rowData!.groupName ? (
            <span
              css={css`
                display: inline-flex;
                align-items: center;
                background: ${theme.colors.neutral5};
                border-radius: ${theme.borderRadius.small};
                padding: ${theme.spacing(0.5, 1)};
              `}
            >
              <Truncated
                as='span'
                tooltipOptions={{ placement: 'top' }}
                css={css`
                  max-width: 200px;
                  font-size: ${theme.font.size.small};
                  color: ${theme.colors.neutral60};
                  @media (max-width: 445px) {
                    max-width: 120px;
                  }
                `}
              >
                {rowData!.groupName}
              </Truncated>
            </span>
          ) : null,
      },
      {
        id: 'actions',
        Header: t('Actions'),
        accessor: 'id',
        disableSortBy: true,
        maxWidth: 85,
        cellAlign: 'center',
        headerAlign: 'center',
        cellRenderer: (_, rowData) => (
          <RemoveActionButton
            isRemoving={
              removingLocationId === rowData!.id ||
              (orphanCheck.isChecking && orphanCheck.locationIdBeingChecked === rowData!.id)
            }
            isLast={isLastLocation}
            lastItemMessage={t('A workspace must be assigned to at least one location.')}
            removeLabel={t('Remove location')}
            onRemove={() => handleRemoveLocation(rowData!.id)}
          />
        ),
      },
    ],
    [
      t,
      handleRemoveLocation,
      removingLocationId,
      isLastLocation,
      orphanCheck.isChecking,
      orphanCheck.locationIdBeingChecked,
    ]
  );

  return (
    <>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          padding: ${theme.spacing(2, 3)};
          width: 100%;
          max-width: 750px;
          background: ${theme.colors.white};
          box-shadow: ${theme.shadows.light};
          border-radius: ${theme.borderRadius.medium};
          box-sizing: border-box;
          @media (max-width: 600px) {
            padding: ${theme.spacing(1.5, 2)};
          }
        `}
      >
        <Tabs initialTab='users'>
          <Tabs.Bar>
            <Tabs.Tab id='users'>{t('Users')}</Tabs.Tab>
            <Tabs.Tab id='locations'>{t('Locations')}</Tabs.Tab>
          </Tabs.Bar>

          {/* ── Users tab ─────────────────────────────────────────────────── */}
          <Tabs.Panel controller='users'>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: ${theme.spacing(2)};
                padding-top: ${theme.spacing(2)};
                width: 100%;
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  gap: ${theme.spacing(1)};
                `}
              >
                <Text
                  as='h2'
                  weight='bold'
                  css={css`
                    font-size: 20px;
                    margin: 0;
                  `}
                >
                  {t('Assigned Users')}
                </Text>
                <Button
                  variant='secondary'
                  iconName='plus-small'
                  onClick={addUsersModal.openModal}
                  trackingId={trackingIds.editUsers}
                  css={css`
                    flex-shrink: 0;
                  `}
                >
                  {t('Add User')}
                </Button>
              </div>

              {/* Error banner — shown when bulk query fails */}
              {resolveResult.isBulkQueryError && (
                <UserResolutionErrorBanner
                  onRetry={resolveResult.refetchBulkQuery}
                  isRetrying={resolveResult.isBulkQueryLoading}
                />
              )}

              <div ref={tableContainerRef}>
                <Table
                  data={assignedUsers}
                  colConfig={usersColConfig}
                  styleConfig={usersStyleConfig}
                  isPaginated={false}
                  fullHeight
                  fullHeightConfig={{ maxHeight: 436, minHeight: 98 }}
                />
              </div>
            </div>
          </Tabs.Panel>

          {/* ── Locations tab ─────────────────────────────────────────────── */}
          <Tabs.Panel controller='locations'>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: ${theme.spacing(2)};
                padding-top: ${theme.spacing(2)};
                width: 100%;
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  gap: ${theme.spacing(1)};
                `}
              >
                <Text
                  as='h2'
                  weight='bold'
                  css={css`
                    font-size: 20px;
                    margin: 0;
                  `}
                >
                  {t('Assigned Locations')}
                </Text>
                <Button
                  variant='secondary'
                  iconName='plus-small'
                  onClick={addLocationsModal.openModal}
                  trackingId={trackingIds.editLocations}
                  css={css`
                    flex-shrink: 0;
                  `}
                >
                  {t('Add Location')}
                </Button>
              </div>

              <Table
                data={locationRows}
                colConfig={locationsColConfig}
                styleConfig={locationsStyleConfig}
                isPaginated={false}
                fullHeight
                fullHeightConfig={{ maxHeight: 436, minHeight: 98 }}
              />
            </div>
          </Tabs.Panel>
        </Tabs>
      </div>

      <WorkspaceUsersModal
        modalProps={addUsersModal.modalProps}
        locationIds={workspace.locationIds}
        initialUsers={assignedUsers.map((u) => ({
          id: u.id,
          firstName: u.firstName,
          lastName: u.lastName,
          username: u.email,
        }))}
        onSave={handleSaveUsers}
        onClose={addUsersModal.closeModal}
      />

      <WorkspaceLocationsModal
        modalProps={addLocationsModal.modalProps}
        locationsByGroup={locationsByGroup}
        currentLocationIds={workspace.locationIds}
        onSave={handleSaveLocations}
        onClose={addLocationsModal.closeModal}
      />

      {/* ── Remove location confirmation (orphaned users) ──────────────────── */}
      <ConfirmationModal
        {...removeLocationConfirmModal.modalProps}
        title={t('Remove location?')}
        destructive
        confirmLabel={t('Remove location & users')}
        cancelLabel={t('Cancel')}
        message={
          <>
            {t('Removing this location will also remove')}{' '}
            <strong>{formatOrphanedUsersMessage(orphanCheck.orphanedUsers)}</strong>{' '}
            {t(
              'from this workspace, as they are not assigned to any other workspace locations. This action cannot be undone.'
            )}
          </>
        }
        onConfirm={() => {
          const locationId = orphanCheck.locationIdBeingChecked;
          const orphanedIds = orphanCheck.orphanedUsers.map((u) => u.id);
          orphanCheck.resetCheck();
          if (locationId) doRemoveLocation(locationId, orphanedIds);
        }}
        onCancel={orphanCheck.resetCheck}
        onClose={() => {
          removeLocationConfirmModal.modalProps.onClose();
          orphanCheck.resetCheck();
        }}
      />
    </>
  );
}
